import React from 'react';
import classNames from 'classnames';
import {
  CaretDown,
  CaretUp,
  CheckXIcon,
  NewCheckIcon,
  Tooltip,
} from 'components';
import dayjs from 'dayjs';
import {
  MemberProfile,
  SessionAppointmentLabel,
  UserAccountType,
} from 'interfaces';
import {
  isDigitalPracticeOutOfPocketMember,
  isInsuranceZocdocMember,
} from 'utils';

type Stage = {
  name: string;
  status: 'completed' | 'pending' | 'denied';
  shouldBeChecked: boolean;
  tooltip: string;
};

export type RevenueItemProps = {
  client: string;
  memberType: string;
  appointmentType: string;
  insuranceName: string;
  appointmentLabel?: SessionAppointmentLabel[];
  sessionDate: string;
  sessionTime: string;
  duration: string;
  sessionId: string;
  stages: Stage[];
  isBillable?: boolean;
  user: MemberProfile;
};

export const RevenueItem: React.FC<RevenueItemProps> = ({
  client,
  memberType,
  sessionId,
  appointmentType,
  insuranceName,
  sessionDate,
  sessionTime,
  appointmentLabel = [],
  duration,
  stages,
  isBillable,
  user,
}) => {
  const [showMore, setShowMore] = React.useState(false);

  let sessionMember;

  if (isDigitalPracticeOutOfPocketMember(user)) {
    sessionMember = 'Digital Practice Out-Of-Pocket';
  } else if (
    memberType === UserAccountType.wellnite &&
    !isInsuranceZocdocMember(user)
  ) {
    sessionMember = 'Wellnite Member Out-Of-Pocket';
  } else {
    sessionMember = insuranceName;
  }

  const shouldShowSection =
    isBillable || (!isBillable && appointmentLabel.length === 0);

  const lastCompletedIdx =
    [...stages]
      .map((stage, idx) => ({idx, status: stage.status}))
      .filter(stage => stage.status === 'completed')
      .map(stage => stage.idx)
      .pop() ?? 0;

  const renderIcon = (
    shouldBeChecked: Stage['shouldBeChecked'],
    status: Stage['status'],
  ) => {
    if (status === 'pending') {
      return (
        <div className="w-5 h-5 border border-gray-300 bg-gray-300 rounded-full" />
      );
    }

    if (shouldBeChecked && status === 'completed') {
      return (
        <div className="w-5 h-5 border border-green-100/50 bg-green-100/50 flex items-center justify-center rounded-full p-1">
          <NewCheckIcon />
        </div>
      );
    }
    if (!shouldBeChecked && status === 'denied') {
      return (
        <div className="w-5 h-5 border border-gray-300 flex items-center justify-center rounded-full p-1">
          <CheckXIcon />
        </div>
      );
    }
    return (
      <div className="w-5 h-5 border border-gray-300 bg-gray-300 rounded-full" />
    );
  };

  return (
    <div className="bg-[#f0f2f9] p-4 rounded-2xl text-[#7c7c7c] mb-6 shadow-sm">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <div className="mb-2">
          <div className="text-lg text-black font-semibold">
            <p>{client}</p>
          </div>
          <div className="text-sm text-gray-600">
            <p>{sessionMember}</p>
          </div>
        </div>
        <div className="text-left sm:text-right">
          <div>
            {' '}
            <p className="text-[15px] mb-2 font-semibold">
              Session ID:&nbsp;
              <span className="text-black font-semibold">{sessionId}</span>
            </p>
          </div>
          <div className="flex justify-start sm:justify-end">
            {isBillable ? (
              <p className="text-[15px]  w-fit mb-2 font-medium text-black bg-[#F7E3BA] px-3 py-2 mt-1 rounded-full">
                Billable session
              </p>
            ) : !isBillable && appointmentLabel.length > 0 ? (
              <p className="text-[15px] w-fit mb-2 font-medium text-black bg-[#E3D1FF] px-3 py-2 mt-1 rounded-full">
                Non-billable session
              </p>
            ) : null}
          </div>
          {appointmentLabel.length
            ? appointmentLabel?.map(label => (
                <div key={label._id} className="text-[15px] font-semibold">
                  {label.name}
                </div>
              ))
            : null}
        </div>
      </div>

      <div
        className={classNames(
          'text-sm text-gray-700 sm:flex flex-col sm:flex-row space-y-5 sm:space-y-0 sm:space-x-10 mb-6',
          {
            hidden: !showMore,
            flex: showMore,
          },
        )}
      >
        <div>
          <p className="font-normal text-[#747474] text-sm">Date</p>
          <p className="text-black font-semibold">
            {dayjs(sessionDate).format('MMMM D, YYYY')}
          </p>
        </div>
        <div>
          <p className="font-normal text-[#747474] text-sm">Time</p>
          <p className="text-black font-semibold">{sessionTime}</p>
        </div>
        <div>
          <p className="font-normal text-[#747474] text-sm">Duration</p>
          <p className="text-black font-semibold">{duration} mins</p>
        </div>
        <div className="">
          <p className="font-normal text-[#747474] text-sm">Member Type</p>
          <p
            className={classNames(
              'text-black font-semibold px-3 py-1 w-fit rounded-full',
              {
                'bg-[#C2D3FF]': memberType === UserAccountType.wellnite,
                'bg-[#FFCAE2]': memberType === UserAccountType.scale,
              },
            )}
          >
            {memberType === UserAccountType.scale
              ? 'Digital Practice'
              : 'Wellnite'}
          </p>
        </div>
        <div>
          <p className="font-normal text-[#747474] text-sm">Appointment Type</p>
          <p className="text-black font-semibold">{appointmentType}</p>
        </div>
      </div>

      {shouldShowSection ? (
        <div
          className={classNames('flex-col p-4 bg-white rounded-lg shadow-sm')}
        >
          <div className="text-sm mb-3 text-gray-800">
            Payment Process Status:
          </div>
          <div className="flex flex-col sm:flex-row flex-grow items-center w-full overflow-x-auto">
            {stages.map((stage, idx) => {
              const isActive = idx === lastCompletedIdx;
              return (
                <div
                  key={idx}
                  className={classNames(
                    'flex-col flex-1 justify-center items-center',
                    {
                      hidden: !showMore && !isActive,
                      flex: showMore || isActive,
                    },
                    'sm:flex',
                  )}
                >
                  <div className="flex justify-center items-center flex-row w-full mb-2">
                    {idx === 0 ? (
                      <div className="hidden sm:flex flex-grow" />
                    ) : (
                      <div className="hidden sm:flex flex-grow border-t border-gray-300" />
                    )}
                    <div className="w-5 h-5 flex items-center justify-center">
                      {renderIcon(stage.shouldBeChecked, stage.status)}
                    </div>
                    {idx === stages.length - 1 ? (
                      <div className="hidden sm:flex flex-grow" />
                    ) : (
                      <div className="hidden sm:flex flex-grow border-t border-gray-300" />
                    )}
                  </div>
                  <Tooltip content={stage.tooltip ?? stage.name}>
                    <span
                      className={classNames(
                        'mt-1 text-xs text-black px-3 py-2 mb-3 sm:mb-0',
                        {
                          'text-red-600 border border-red-600 rounded-full':
                            stage.status === 'denied',
                          'bg-green-100/50 rounded-full':
                            stage.status === 'completed',
                        },
                      )}
                    >
                      {stage.name}
                    </span>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      <div className="flex justify-center item-center sm:hidden mt-4">
        <button
          onClick={() => setShowMore(!showMore)}
          className="text-sm text-black font-medium flex justify-center items-center"
        >
          {showMore ? 'Less Details' : 'More Details'}
          <span className="ml-2">{showMore ? <CaretUp /> : <CaretDown />}</span>
        </button>
      </div>
    </div>
  );
};
